import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { fleetService } from '../../services/fleetService';
import FleetStatusBadge from '../../components/fleets/FleetStatusBadge';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import { Search, Filter, Eye, Plus, Edit, Trash, AlertTriangle, Users, MapPin } from 'lucide-react';

const FleetsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [fleets, setFleets] = useState([]);
    const [totalFleets, setTotalFleets] = useState(0);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, fleetId: null });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10
    });
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });

    useEffect(() => {
        fetchFleets();
    }, [pagination.currentPage, filters]);

    const fetchFleets = async () => {
        setIsLoading(true);
        try {
            const { data, meta } = await fleetService.getFleets({
                page: pagination.currentPage,
                limit: pagination.perPage,
                ...filters
            });

            setFleets(data);
            setTotalFleets(meta.total);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, status: value }));
    };

    const handleFilterReset = () => {
        setFilters({
            status: '',
            search: ''
        });
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const handleViewFleet = (fleetId) => {
        navigate(`/fleets/${fleetId}`);
    };

    const handleEditFleet = (fleetId) => {
        navigate(`/fleets/${fleetId}/edit`);
    };

    const confirmDelete = (fleetId) => {
        setDeleteConfirmation({ show: true, fleetId });
    };

    const handleDeleteFleet = async () => {
        if (!deleteConfirmation.fleetId) return;

        try {
            await fleetService.deleteFleet(deleteConfirmation.fleetId);
            setDeleteConfirmation({ show: false, fleetId: null });
            fetchFleets(); // 重新加載車隊列表
        } catch (error) {
            console.error('Error deleting fleet:', error);
        }
    };

    const handleViewDrivers = (fleetId) => {
        navigate(`/drivers?fleet_id=${fleetId}`);
    };

    const handleViewServiceArea = (fleetId) => {
        navigate(`/fleets/${fleetId}/service-area`);
    };

    const columns = [
        {
            header: '車隊名稱',
            accessor: 'fleet_name',
            cell: (row) => (
                <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 text-blue-500 rounded-full flex items-center justify-center mr-3">
                        {row.fleet_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <p className="font-medium">{row.fleet_name}</p>
                        <p className="text-xs text-gray-500">ID: {row.fleet_id}</p>
                    </div>
                </div>
            )
        },
        {
            header: '司機數',
            accessor: 'driver_count',
            cell: (row) => (
                <div className="text-center">
                    <span className="font-medium">{row.driver_count}</span>
                    <button
                        onClick={() => handleViewDrivers(row.fleet_id)}
                        className="ml-2 text-blue-500 hover:text-blue-700"
                        title="查看司機"
                    >
                        <Users className="w-4 h-4" />
                    </button>
                </div>
            )
        },
        {
            header: '服務區域',
            accessor: 'service_area',
            cell: (row) => (
                <div className="flex items-center">
                    <span className="truncate max-w-xs">{row.service_area}</span>
                    <button
                        onClick={() => handleViewServiceArea(row.fleet_id)}
                        className="ml-2 text-blue-500 hover:text-blue-700"
                        title="查看服務區域"
                    >
                        <MapPin className="w-4 h-4" />
                    </button>
                </div>
            )
        },
        {
            header: '狀態',
            accessor: 'status',
            cell: (row) => (
                <FleetStatusBadge status={row.status} />
            )
        },
        {
            header: '建立時間',
            accessor: 'created_at',
            cell: (row) => (
                <span className="text-sm text-gray-600">
                    {new Date(row.created_at).toLocaleString('zh-TW', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </span>
            )
        },
        {
            header: '操作',
            accessor: 'actions',
            cell: (row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleViewFleet(row.fleet_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="查看詳情"
                    >
                        <Eye className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleEditFleet(row.fleet_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="編輯車隊"
                    >
                        <Edit className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => confirmDelete(row.fleet_id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        title="刪除車隊"
                    >
                        <Trash className="w-5 h-5" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">車隊管理</h1>
                <button
                    onClick={() => navigate('/fleets/new')}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    新增車隊
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex-1 max-w-md relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                                placeholder="搜尋車隊名稱或服務區域..."
                                value={filters.search}
                                onChange={handleSearch}
                            />
                        </div>

                        <select
                            className="border border-gray-300 rounded-md px-3 py-2"
                            value={filters.status}
                            onChange={handleStatusChange}
                        >
                            <option value="">所有狀態</option>
                            <option value="active">運營中</option>
                            <option value="inactive">暫停運營</option>
                            <option value="suspended">已停用</option>
                            <option value="maintenance">系統維護</option>
                        </select>

                        {(filters.status !== '' || filters.search !== '') && (
                            <button
                                onClick={handleFilterReset}
                                className="text-blue-500 hover:underline text-sm"
                            >
                                重置篩選
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        <DataTable
                            columns={columns}
                            data={fleets}
                            emptyMessage="無符合條件的車隊"
                        />
                        <div className="p-4 border-t border-gray-200">
                            <Pagination
                                currentPage={pagination.currentPage}
                                totalItems={totalFleets}
                                perPage={pagination.perPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* 刪除確認對話框 */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0 mt-0.5">
                                <AlertTriangle className="h-6 w-6 text-red-500" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">確認刪除</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    您確定要刪除此車隊嗎？此操作無法恢復，並會影響所有屬於該車隊的司機和訂單。
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 bg-yellow-50 p-4 rounded-md border border-yellow-200">
                            <p className="text-sm text-yellow-700">
                                <strong>警告：</strong> 刪除車隊將導致該車隊的所有司機無法接單，直到他們被分配到新的車隊。
                            </p>
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setDeleteConfirmation({ show: false, fleetId: null })}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                取消
                            </button>
                            <button
                                onClick={handleDeleteFleet}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                            >
                                確認刪除
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FleetsPage;